import { Box, Dialog, Stack, Typography } from '@mui/material';
import { IconButton } from '@tix-ticketing/ui-components';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CrossIcon,
} from '@tix-ticketing/ui-icons/outline';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import type { TicketDto } from 'api/generated';
import { FormattedPrice } from 'lib/helpers/formatted-price';

/**
 * Full-screen 360° view from a seat. `tickets` are the seats the arrows step
 * through — the caller's own list (a reservation in the seat picker, an
 * event's tickets in the cart); only those with a 360° view are offered.
 *
 * The view is the CDN's own viewer page in a cross-origin iframe, so the
 * dialog can't control it: panning and zoom are whatever that page offers.
 */
export const Seat360Dialog = ({
  ticket,
  tickets,
  hallName,
  onClose,
}: {
  ticket: TicketDto;
  tickets: TicketDto[];
  hallName: string;
  onClose: () => void;
}) => {
  const intl = useIntl();
  const dialogRef = useRef<HTMLDivElement>(null);
  const mouseDownRef = useRef(false);
  // The iframe mounts once the dialog has entered and the page scrollbar is
  // gone; mounting it during the transition leaves a scrollbar-wide unpainted
  // strip on the right of the viewer.
  const [entered, setEntered] = useState(false);

  const allTickets = tickets.filter((t) => t.path360);
  const [selectedId, setSelectedId] = useState(ticket.id);
  // Read from the live list so a ticket type changed meanwhile shows its price.
  const selectedTicket = allTickets.find((t) => t.id === selectedId) ?? ticket;

  const stepTicket = (step: 1 | -1) => {
    const currentIndex = allTickets.findIndex((t) => t.id === selectedId);
    const nextIndex =
      (currentIndex + step + allTickets.length) % allTickets.length;
    setSelectedId(allTickets[nextIndex]?.id);
  };

  // Move focus out of iframe for keyboard events to work
  useEffect(() => {
    let interval: NodeJS.Timeout;

    const start = () => {
      interval = setInterval(() => {
        if (!mouseDownRef.current) {
          dialogRef.current?.focus();
        }
      }, 500);
    };

    start();
    return () => clearInterval(interval);
  }, []);

  return (
    <Dialog
      open
      fullScreen
      onClose={onClose}
      ref={dialogRef}
      tabIndex={-1}
      slotProps={{ transition: { onEntered: () => setEntered(true) } }}
    >
      <IconButton
        color="white"
        variant="contained"
        sx={{
          position: 'absolute',
          top: (theme) => theme.spacing(3),
          right: (theme) => theme.spacing(3),
        }}
        onClick={onClose}
        label={intl.formatMessage({
          id: 'common.close',
          defaultMessage: 'Close',
        })}
      >
        <CrossIcon />
      </IconButton>
      {entered && (
        <Box
          component="iframe"
          src={selectedTicket.path360 ?? ''}
          title={intl.formatMessage({
            id: 'seatPicker.seat360.iframeTitle',
            defaultMessage: '360° view from the seat',
          })}
          sx={{
            display: 'block',
            width: '100%',
            height: '100%',
            border: 0,
          }}
        />
      )}
      <Stack
        direction="row"
        sx={(theme) => ({
          position: 'absolute',
          bottom: theme.spacing(3),
          left: '50%',
          transform: 'translateX(-50%)',
          alignItems: 'center',
          gap: 2,
          p: 2,
          // A px string: a bare number in `sx` is scaled by shape.borderRadius.
          borderRadius: `${theme.buttonRadius.main}px`,
          backgroundColor: theme.palette.background.elevated,
          boxShadow: theme.shadows[12],
          [theme.breakpoints.down('sm')]: {
            // Spans the screen inside the gutters rather than overflowing it.
            left: theme.spacing(2),
            right: theme.spacing(2),
            bottom: theme.spacing(2),
            transform: 'none',
            gap: 1,
            p: 1,
            borderRadius: `${theme.buttonRadius.mobile}px`,
          },
        })}
      >
        {allTickets.length > 1 && (
          <IconButton
            variant="contained"
            color="monochrome"
            onClick={() => stepTicket(-1)}
            label={intl.formatMessage({
              id: 'common.previous',
              defaultMessage: 'Previous',
            })}
          >
            <ArrowLeftIcon />
          </IconButton>
        )}
        {hallName && (
          <TextBlock
            title={intl.formatMessage({
              id: 'seatPicker.hall',
              defaultMessage: 'Hall',
            })}
            description={hallName}
            alignment="left"
            shrink
          />
        )}
        {selectedTicket.section && (
          <TextBlock
            title={intl.formatMessage({
              id: 'common.section',
              defaultMessage: 'Section',
            })}
            description={selectedTicket.section}
            alignment="left"
            shrink
          />
        )}
        {selectedTicket.row && (
          <TextBlock
            title={intl.formatMessage({
              id: 'seatPicker.row',
              defaultMessage: 'Row',
            })}
            description={selectedTicket.row}
          />
        )}
        {selectedTicket.seat && (
          <TextBlock
            title={intl.formatMessage({
              id: 'seatPicker.seat',
              defaultMessage: 'Seat',
            })}
            description={selectedTicket.seat}
          />
        )}
        <TextBlock
          title={intl.formatMessage({
            id: 'common.price',
            defaultMessage: 'Price',
          })}
          description={
            <FormattedPrice amount={selectedTicket.priceWithFeesAndTax ?? 0} />
          }
          alignment="right"
        />
        {allTickets.length > 1 && (
          <IconButton
            variant="contained"
            color="monochrome"
            onClick={() => stepTicket(1)}
            label={intl.formatMessage({
              id: 'common.next',
              defaultMessage: 'Next',
            })}
          >
            <ArrowRightIcon />
          </IconButton>
        )}
      </Stack>
    </Dialog>
  );
};

const TextBlock = ({
  title,
  description,
  alignment = 'center',
  shrink = false,
}: {
  title: string;
  description: React.ReactNode;
  alignment?: 'left' | 'center' | 'right';
  /** Lets a long value give way and truncate on narrow screens. */
  shrink?: boolean;
}) => {
  const truncate = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  } as const;
  return (
    <Stack
      sx={(theme) => ({
        gap: 0.25,
        px: 3,
        whiteSpace: 'nowrap',
        textAlign: alignment,
        flexShrink: shrink ? 1 : 0,
        minWidth: shrink ? 0 : undefined,
        [theme.breakpoints.down('sm')]: {
          px: 1,
          // Shrinkable blocks split the leftover width evenly, so a long
          // section name doesn't squeeze the hall name down to its first letter.
          ...(shrink && { flex: '1 1 0' }),
        },
      })}
    >
      <Typography
        variant="body3"
        sx={{ color: 'text.secondary', ...(shrink && truncate) }}
      >
        {title}
      </Typography>
      <Typography variant="h6" sx={shrink ? truncate : undefined}>
        {description}
      </Typography>
    </Stack>
  );
};
